export const isLocalEnvironment = process.env.NODE_ENV === "development";

export const envPrefix: string = "#{ENV_PREFIX}";

const trimmedPrefix = envPrefix.toString().trim();

export let environment = isLocalEnvironment
  ? "qa."
  : /* istanbul ignore next — selects on `envPrefix`, a deploy-time literal that
       cannot be varied at test time; same constraint `resolveAmplitudeDeploymentKey`
       is exported to work around (PPI-1848 coverage pass) */
  trimmedPrefix && !["prd", "prod"].includes(trimmedPrefix.toLowerCase())
  ? `${trimmedPrefix}.`
  : "";

/* istanbul ignore next — defensive and unreachable: the ternary above yields
   "qa.", "<prefix>." or "", never "." (a "." would need a truthy prefix that
   is also the empty string). Kept as a guard rather than deleted. */
environment = environment !== "." ? environment : "";

export const isProduction = environment === "";

const ENABLE_ATTESTATION_LOCAL = true;
const ENABLE_ATTESTATION_NON_PRODUCTION = true;
const ENABLE_ATTESTATION_PRODUCTION = true;

/**
 * Determines whether attestation checks should be on or off
 * @returns A boolean based on the above flags and the current environment
 */
function determineAttestation() {
  if (isLocalEnvironment) return ENABLE_ATTESTATION_LOCAL;
  /* istanbul ignore next — reachable only when `isProduction`, which derives
     from the deploy-time ENV_PREFIX literal (see `environment` above). All
     three flags are currently true, so this returns the same value either way. */
  if (isProduction) return ENABLE_ATTESTATION_PRODUCTION;
  return ENABLE_ATTESTATION_NON_PRODUCTION;
}

export const ENABLE_ATTESTATION = determineAttestation();

/* Amplitude Experiment deployment keys per environment.
 * Client-side public keys — they ship in the browser bundle, so committing them is fine.
 * Source: Amplitude > Experiment > Deployments > <deployment> > Deployment API Key.
 * Naming convention: members-portal-{qa,stg,prd}. */
const amplitudeDeploymentKeys = {
  qa: "client-VHHRfFS2UQ3Lfke7W79adp98OHpPBYWJ",
  stg: "client-Fg8NXyq6EHKiYdK34Pv8pS4t41UUnffZ",
  prd: "client-k7lLcQiSYu9QP5IFVOL7o3D2ci8RvsPM",
} as const;

/* The ENV_PREFIX values that mean production. "prd" is what the stg/prd workflow
 * actually deploys with (.github/workflows/ph-member-portal-stg-prd.yml sets ENV: prd);
 * "" only occurs when the token was never replaced. Both are treated as production,
 * matching how `environment` above collapses them to the bare hostname. */
const productionPrefixes = ["", "prd", "prod"];

/**
 * Maps the deploy-time ENV_PREFIX token to its Amplitude deployment key.
 *
 * Production deploys as ENV_PREFIX=prd, not as an empty string. This previously
 * matched only the empty case, so production fell through to "" -- no deployment key,
 * ENABLE_AMPLITUDE false, and every feature flag resolving to disabled no matter how
 * it was configured in Amplitude (PPI-1836: FlexBenefits was invisible in production
 * while working in QA, where ENV_PREFIX=qa matched). `environment` already handled
 * "prd"/"prod", which is why the API hostname was right the whole time and the
 * mismatch stayed hidden.
 *
 * Exported for tests: the prefix is a build-time literal in this module, so calling
 * this directly is the only way to exercise a genuinely deployed value.
 */
export function resolveAmplitudeDeploymentKey(prefix: string): string {
  const normalized = prefix.toString().trim().toLowerCase();
  if (normalized === "qa") return amplitudeDeploymentKeys.qa;
  if (normalized === "stg") return amplitudeDeploymentKeys.stg;
  if (productionPrefixes.includes(normalized))
    return amplitudeDeploymentKeys.prd;
  return "";
}

function getAmplitudeDeploymentKey(): string {
  if (isLocalEnvironment) return amplitudeDeploymentKeys.qa;
  return resolveAmplitudeDeploymentKey(envPrefix);
}

export const AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY = getAmplitudeDeploymentKey();

export const ENABLE_AMPLITUDE = isLocalEnvironment
  ? process.env.REACT_APP_ENABLE_AMPLITUDE === "true"
  : /* istanbul ignore next — deployed-env branch, covered post-replacement */
    Boolean(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY);

/* --------------------------------------------------------------------------
 * Session idle timeout, token renewal & cookie hardening (PPI-1848, epic
 * PPI-1845 — HITRUST authentication).
 * ---------------------------------------------------------------------- */

// Documented range for REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES. Below 5 minutes
// is punitive for a member filling out a slow form; above 120 minutes defeats
// the HITRUST idle-session control this ticket exists to satisfy.
export const SESSION_IDLE_TIMEOUT_MIN_MINUTES = 5;
export const SESSION_IDLE_TIMEOUT_MAX_MINUTES = 120;
export const SESSION_IDLE_TIMEOUT_FALLBACK_MINUTES = 15;

/**
 * Resolves REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES to a bounded minute value.
 * An absent, non-numeric, or out-of-range value falls back to 15 minutes
 * rather than disabling the control or failing the build.
 *
 * This is a real CRA env var rather than a per-deployment hardcoded map like
 * the Amplitude keys above, because PPI-1848 specifically calls for an
 * environment-level setting Compliance can change per portal without a code
 * release. Note that CRA inlines REACT_APP_* at build time, so changing it
 * still requires rebuilding that environment — it is not a live toggle.
 *
 * Exported for tests, following the same pattern as
 * `resolveAmplitudeDeploymentKey`.
 */
export function resolveSessionIdleTimeoutMinutes(
  rawValue: string | undefined
): number {
  const parsed = Number(rawValue);
  const isValid =
    rawValue !== undefined &&
    rawValue.trim() !== "" &&
    Number.isFinite(parsed) &&
    parsed >= SESSION_IDLE_TIMEOUT_MIN_MINUTES &&
    parsed <= SESSION_IDLE_TIMEOUT_MAX_MINUTES;

  return isValid ? parsed : SESSION_IDLE_TIMEOUT_FALLBACK_MINUTES;
}

export const SESSION_IDLE_TIMEOUT_MINUTES = resolveSessionIdleTimeoutMinutes(
  process.env.REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES
);

// Fixed by product decision (PPI-1848) — not required to be environment
// configurable, unlike the idle timeout itself.
export const SESSION_IDLE_WARNING_SECONDS = 120;

// Mirrors pivot-health-authentication's sliding-renewal window
// (JwtRequestFilter.kt: getSecondsToExpired(token) in `1 until 600`). This is
// a *read* of an existing backend contract this repo does not own — if that
// window ever changes server-side, this constant must move with it or
// renewal pokes will fire outside the window the server is willing to honor.
export const SESSION_RENEWAL_WINDOW_SECONDS = 600;

// How often the idle/renewal clock re-derives state from timestamps. Short
// and cheap by design: a suspended laptop must be detected as already-idle
// within one tick of waking, not after a long-lived setTimeout that never
// fired while the machine was asleep.
export const SESSION_ACTIVITY_POLL_INTERVAL_MS = 5000;

// Real user-input activity is written to the shared cookie at most this
// often, so a scroll/keydown burst does not hammer document.cookie.
export const SESSION_ACTIVITY_WRITE_THROTTLE_MS = 5000;

// How often a tab evaluates whether the token needs renewing. Stamped on
// every check rather than only when a renewal fires — stamping only on fire
// would leave the check un-throttled for the ~99% of a session where the
// token is nowhere near expiry, reading and decoding the JWT every 5s. Also
// caps redundant pokes once inside the backend's 10-minute window: once per
// poll tick for 10 minutes would be up to 120 requests from one idle tab.
export const SESSION_RENEWAL_CHECK_INTERVAL_MS = 60000;

// Cookie key the login page reads (and clears) to tell a returning member why
// they were signed out. Written by both the idle clock (useSessionActivity)
// and HttpClient's 401/407 handling, so it needs a single shared home; it
// lives here rather than in strings.ts because it is a cookie key, not
// user-facing copy.
export const SESSION_ENDED_REASON_COOKIE_KEY = "session_ended_reason";

export type SessionEndedReason = "idle" | "expired";

// The two values that cookie ever holds. Exported so the idle clock, the
// HttpClient 401/407 branch and the login-page banner all agree on the
// spelling rather than repeating string literals in three layers.
export const SESSION_ENDED_REASON_IDLE: SessionEndedReason = "idle";
export const SESSION_ENDED_REASON_EXPIRED: SessionEndedReason = "expired";
